import { signupRoute } from './signup-diagnostics'

export const REQUEST_DIAGNOSTIC_EVENT = 'diagnostic:request_failure'
type Properties = Record<string, string | number | boolean>

// Read only routing metadata. Never inspect bodies, cookies or authorization.
export function classifyRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  origin: string,
) {
  try {
    const request = typeof Request !== 'undefined' && input instanceof Request ? input : undefined
    const url = new URL(request ? request.url : String(input), origin)
    const headers = new Headers(init?.headers ?? request?.headers)
    if (url.origin === origin) {
      if (headers.has('Next-Action')) return 'server_action'
      if (headers.get('RSC') === '1' || url.searchParams.has('_rsc')) return 'next_navigation'
    }
    if (
      ['clerk.wwjdfilm.com', 'clerk.christspiracy.com'].includes(url.hostname) &&
      url.pathname.startsWith('/v1/')
    ) {
      return 'clerk_api'
    }
  } catch {
    // Unclassifiable requests still run unmodified.
  }
  return undefined
}

export function createDiagnosticFetch(
  fetcher: typeof fetch,
  origin: string,
  state: () => { pathname: string; online: boolean; visibility: string },
  capture: (properties: Properties) => void,
): typeof fetch {
  let emitted = 0
  const report = (kind: string | undefined, stage: string, status: number) => {
    if (!kind || emitted >= 5) return
    emitted++
    try {
      const current = state()
      capture({
        diagnostic_version: 1,
        request_kind: kind,
        stage,
        status,
        route: signupRoute(current.pathname),
        online: current.online,
        visibility: current.visibility === 'visible' ? 'visible' : 'hidden',
      })
    } catch {
      // Diagnostics cannot affect the request or replace its original error.
    }
  }
  return async (input, init) => {
    const kind = classifyRequest(input, init, origin)
    try {
      const response = await fetcher(input, init)
      if (response.status >= 400) report(kind, 'http_error', response.status)
      return response
    } catch (error) {
      // Aborted navigation is expected, not a transport failure.
      if (!(error instanceof Error && error.name === 'AbortError'))
        report(kind, 'fetch_rejected', 0)
      throw error
    }
  }
}
